import detectWebview from 'is-ua-webview';
import throttle from 'lodash/throttle';
import { v4 as uuidv4 } from 'uuid';
import { nextTick, watch } from 'vue';
import { router } from '@/app/router';
import { useDeveloperStore } from '@/app/stores/developer';
import { sentryService } from '@/services/telemetry/sentry';
import { PubSub } from '@/shared/lib/pub-sub';
import { reactive } from '@/shared/lib/reactive';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import { track } from '@/shared/utils/track-telemetry';
import tryParsingJson from '@/shared/utils/try-parsing-json';

export class KioskHardware extends PubSub {
  MODES = {
    /**
     * Physical kiosk that runs on a Mini PC at a venue (default)
     */
    MINI_PC_KIOSK: 'MINI_PC_KIOSK',

    /**
     * Virtual kiosk that runs on customer's device once they scan a QR code hanging on a wall at a venue to skip the line
     * Usually mobile, but technically can work on desktop as well
     */
    VIRTUAL_KIOSK: 'VIRTUAL_KIOSK',
  };

  GLOBAL_CLASSNAMES = {
    PORTRAIT: 'App--portrait',
    LANDSCAPE: 'App--landscape',
    MINI_PC: 'App--mini-pc',
    VERTICAL_KIOSK: 'App--vertical-kiosk', // Large vertical display (55" 4K)
  };

  GLOBAL_DATA_ATTRIBUTES = {
    DISPLAY_PROFILE: 'data-kiosk-display-profile',
    RUNTIME: 'data-kiosk-runtime',
  };

  EVENTS = {
    INITIALIZED: 'INITIALIZED',
  };

  @reactive
  kioskId = null;

  @reactive
  kioskMode = null;

  @reactive
  isMiniPCKiosk = null;

  @reactive
  isVirtualKiosk = null;

  @reactive
  isDevMode = null;

  @reactive
  device = {};

  @reactive
  isMobileDevice = null;

  @reactive
  viewportWidth = null;

  @reactive
  viewportHeight = null;

  @reactive
  viewportIsPortrait = null;

  @reactive
  isVerticalKiosk = null; // Large vertical display (55" 4K) vs mobile portrait

  // Cleanup handles for watchers
  _unwatchDeveloperSettings = null;

  constructor() {
    super();
    exposeToGlobalScope(this, 'KioskHardware');
  }

  initialize() {
    this.kioskId = this.getKioskId();
    this._applyKioskMode();
    this.isDevMode = this.getIsDevMode();

    const { width, height, isPortrait, isVerticalKiosk } =
      this.getViewportInfo();
    this.viewportWidth = width;
    this.viewportHeight = height;
    this.viewportIsPortrait = isPortrait;
    this.isVerticalKiosk = isVerticalKiosk;
    this.isMobileDevice = this.detectMobileUserAgent();

    // Add Sentry breadcrumb for hardware initialization start
    sentryService.addBreadcrumb('hardware', 'Hardware detection started', {
      kioskId: this.kioskId,
      kioskMode: this.kioskMode,
      viewport: `${width}x${height}`,
      isPortrait,
      isVerticalKiosk,
      isMobileDevice: this.isMobileDevice,
    });

    this.updateGlobalCssClasses();
    const triggerInitialized = () =>
      nextTick(() => {
        this.trigger(this.EVENTS.INITIALIZED, null, true);
      });
    this.extractDeviceMeta()
      .then((device) => {
        this.device = device;

        // Set Sentry device context with all hardware information
        this._setSentryDeviceContext(device);

        // Track initial device metrics
        this._trackDeviceMetrics();

        this.watchViewportResize();
        this.watchDeveloperSettings();

        // Add Sentry breadcrumb for hardware initialization complete
        sentryService.addBreadcrumb(
          'hardware',
          'Hardware detection completed',
          {
            platform: device.platform,
            browser: device.browser,
            countryCode: device.country_code,
          }
        );

        triggerInitialized();
      })
      .catch((error) => {
        // Capture hardware detection failure in Sentry
        sentryService.captureException(error, {
          tags: { component: 'KioskHardware', action: 'initialize' },
          extra: {
            kioskId: this.kioskId,
            kioskMode: this.kioskMode,
            viewportWidth: this.viewportWidth,
            viewportHeight: this.viewportHeight,
          },
          level: 'error',
        });
        console.error('[KioskHardware] Hardware detection failed:', error);

        // Still trigger initialization to avoid blocking the app
        triggerInitialized();
      });
  }

  /**
   * Set Sentry device context with comprehensive hardware information
   * @param {Object} device - Device metadata from extractDeviceMeta
   * @private
   */
  _setSentryDeviceContext(device) {
    track('[KioskHardware] Sentry context failed:', () => {
      sentryService.setDeviceContext({
        platform: device.platform,
        browser: device.browser,
        countryCode: device.country_code,
        isMobile: this.isMobileDevice,
        viewportWidth: this.viewportWidth,
        viewportHeight: this.viewportHeight,
      });

      // Set additional hardware-specific context
      // Owner has full visibility rights over their own kiosk infrastructure
      sentryService.setContext('hardware', {
        kiosk_mode: this.kioskMode,
        is_mini_pc_kiosk: this.isMiniPCKiosk,
        is_virtual_kiosk: this.isVirtualKiosk,
        is_vertical_kiosk: this.isVerticalKiosk,
        is_mobile_device: this.isMobileDevice,
        is_real_mobile: this.isRealMobileDevice(),
        is_dev_mode: this.isDevMode,
        screen_width: device.screen_width,
        screen_height: device.screen_height,
        device_uuid: device.device_uuid || null, // Full UUID - owner has full visibility rights
        user_agent: device.user_agent, // Full user agent
        remote_ip: device.remote_ip, // Include IP - owner owns these kiosks
        browser_family: this._getBrowserFamily(device.user_agent),
        // Hardware capabilities
        device_memory_gb: device.device_memory_gb,
        hardware_concurrency: device.hardware_concurrency,
        max_touch_points: device.max_touch_points,
        pixel_ratio: device.pixel_ratio,
      });

      // Set hardware-related tags for filtering in Sentry
      sentryService.setTag('kiosk_mode', this.kioskMode);
      sentryService.setTag('is_mobile', this.isMobileDevice ? 'true' : 'false');
      sentryService.setTag(
        'is_vertical_kiosk',
        this.isVerticalKiosk ? 'true' : 'false'
      );
    });
  }

  /**
   * Track device metrics in Sentry
   * @private
   */
  _trackDeviceMetrics() {
    const trackPixels = (name, value) =>
      sentryService.trackMetric(name, value, 'pixel', {
        kiosk_mode: this.kioskMode,
      });
    trackPixels('device.viewport_width', this.viewportWidth);
    trackPixels('device.viewport_height', this.viewportHeight);
    trackPixels('device.screen_width', window.screen.width);
    trackPixels('device.screen_height', window.screen.height);
  }

  /**
   * Extract browser family from user agent for GDPR-compliant logging.
   * Returns only the browser name without version or detailed fingerprinting data.
   * @param {string} userAgent - The full user agent string
   * @returns {string} Browser family name (e.g., 'Chrome', 'Safari', 'Firefox')
   * @private
   */
  _getBrowserFamily(userAgent) {
    if (!userAgent) {
      return 'unknown';
    }
    const ua = userAgent.toLowerCase();
    if (ua.includes('edg') || ua.includes('edge')) {
      return 'Edge';
    }
    if (ua.includes('opr') || ua.includes('opera')) {
      return 'Opera';
    }
    if (ua.includes('chrome') || ua.includes('crios')) {
      return 'Chrome';
    }
    if (ua.includes('safari')) {
      return 'Safari';
    }
    if (ua.includes('firefox')) {
      return 'Firefox';
    }
    if (ua.includes('msie') || ua.includes('trident')) {
      return 'IE';
    }
    if (ua.includes('samsungbrowser')) {
      return 'Samsung';
    }
    return 'other';
  }

  async extractDeviceMeta() {
    const { remote_ip, country_code } = await this.fetchGeoInfo();
    return {
      remote_ip,
      country_code,
      device_uuid: this.getOrCreateDeviceUuid(),
      user_agent: navigator.userAgent,
      platform: this.detectPlatform(),
      browser: this.detectBrowser(),
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      // Hardware capabilities
      device_memory_gb: navigator.deviceMemory || null,
      hardware_concurrency: navigator.hardwareConcurrency || null,
      max_touch_points: navigator.maxTouchPoints || 0,
      color_depth: screen.colorDepth,
      pixel_ratio: window.devicePixelRatio,
      screen_orientation: screen.orientation?.type || null,
      available_width: screen.availWidth,
      available_height: screen.availHeight,
    };
  }

  detectPlatform() {
    try {
      const platform = navigator.platform.toLowerCase();
      const userAgent = navigator.userAgent.toLowerCase();
      const detectors = [
        [/android/.test(userAgent), 'android'],
        [/iphone|ipad|ipod/.test(platform), 'ios'],
        [/win/.test(platform), 'windows'],
        [/mac/.test(platform), 'macos'],
        [/linux/.test(platform), 'linux'],
      ];
      const match = detectors.find(([isMatch]) => isMatch);
      return match ? match[1] : 'unknown';
    } catch (error) {
      sentryService.captureException(error, {
        tags: { component: 'KioskHardware', action: 'detectPlatform' },
        extra: {
          platform: navigator.platform,
          browser: this._getBrowserFamily(navigator.userAgent),
        },
        level: 'warning',
      });
      return 'unknown';
    }
  }

  detectBrowser() {
    try {
      const userAgent = navigator.userAgent.toLowerCase();
      const isWebview = detectWebview(navigator.userAgent);
      const isChrome =
        /chrome|crios/.test(userAgent) && !/edge|edgios/.test(userAgent);
      const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent);

      const detectors = [
        [isChrome && isWebview, 'chrome-webview'],
        [isChrome, 'chrome'],
        [isSafari && isWebview, 'safari-webview'],
        [isSafari, 'safari'],
        [/firefox/.test(userAgent), 'firefox'],
        [/edg|edge/.test(userAgent), 'edge'],
        [/opr|opera/.test(userAgent), 'opera'],
        [/msie|trident/.test(userAgent), 'ie'],
        [/samsungbrowser/.test(userAgent), 'samsung'],
        [/ucbrowser/.test(userAgent), 'uc'],
        [/qqbrowser/.test(userAgent), 'qq'],
        [/baidubrowser/.test(userAgent), 'baidu'],
        [/yabrowser/.test(userAgent), 'yandex'],
      ];
      const match = detectors.find(([isMatch]) => isMatch);
      return match ? match[1] : 'unknown';
    } catch (error) {
      sentryService.captureException(error, {
        tags: { component: 'KioskHardware', action: 'detectBrowser' },
        extra: { browser: this._getBrowserFamily(navigator.userAgent) },
        level: 'warning',
      });
      return 'unknown';
    }
  }

  detectMobileUserAgent() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isMobileUA =
      /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        userAgent.toLowerCase()
      );

    // Also detect iPads in desktop mode (they have touch but report desktop UA)
    const isIPadDesktopMode =
      navigator.maxTouchPoints > 2 &&
      /Macintosh/.test(userAgent) &&
      'ontouchend' in document;

    return isMobileUA || isIPadDesktopMode;
  }

  /**
   * More robust check: is this a REAL mobile device (not emulated)?
   * Uses multiple signals beyond just User-Agent.
   */
  isRealMobileDevice() {
    const isMobileUA = this.detectMobileUserAgent();

    // Real mobile devices have small physical screens
    const screenMinDimension = Math.min(screen.width, screen.height);
    const hasSmallPhysicalScreen = screenMinDimension <= 768;

    // Combine: mobile UA + small screen = real mobile
    // Mobile UA + large screen = likely emulation (treat as desktop)
    return isMobileUA && hasSmallPhysicalScreen;
  }

  getIsDevMode() {
    return location.hostname === 'localhost';
  }

  getOrCreateDeviceUuid() {
    let deviceUuid = tryParsingJson(
      localStorage.getItem('kiosk_deviceUuid'),
      'localStorage.kiosk_deviceUuid'
    );
    if (!deviceUuid) {
      deviceUuid = uuidv4();
      localStorage.setItem('kiosk_deviceUuid', JSON.stringify(deviceUuid));
    }
    return deviceUuid;
  }

  getKioskMode(options) {
    const { ignoreForcedMode } = options || {};
    const developer = useDeveloperStore();
    if (!ignoreForcedMode && developer.forceKioskMode) {
      return developer.forceKioskMode;
    }

    const isFullscreen = window.innerHeight === window.screen.height;
    const isNotMobile = !this.detectMobileUserAgent();

    // Traditional landscape kiosk (horizontal display)
    const isLandscapeKiosk =
      isFullscreen &&
      screen.orientation?.type === 'landscape-primary' &&
      isNotMobile;

    // Vertical kiosk (55" 4K portrait display) - also a Mini PC kiosk, just rotated
    // Uses same detection as getViewportInfo() but simplified
    const { isVerticalKiosk } = this.getViewportInfo();
    const isVerticalMiniPCKiosk =
      isFullscreen && isVerticalKiosk && isNotMobile;

    const isMiniPCKiosk = isLandscapeKiosk || isVerticalMiniPCKiosk;

    return isMiniPCKiosk ? this.MODES.MINI_PC_KIOSK : this.MODES.VIRTUAL_KIOSK;
  }

  _applyKioskMode() {
    this.kioskMode = this.getKioskMode();
    this.isMiniPCKiosk = this.kioskMode === this.MODES.MINI_PC_KIOSK;
    this.isVirtualKiosk = this.kioskMode === this.MODES.VIRTUAL_KIOSK;
  }

  getKioskId() {
    return router.query.kiosk_id || null;
  }

  fetchGeoInfo() {
    const getRowByPrefix = (response, rowPrefix) => {
      return (
        response
          .split(/\n/)
          .find((row) => row.startsWith(`${rowPrefix}=`))
          ?.split('=')[1] || null
      );
    };

    const cloudFlareApiUrl = this.getIsDevMode()
      ? 'https://1.1.1.1/cdn-cgi/trace'
      : '/cdn-cgi/trace';

    return fetch(cloudFlareApiUrl)
      .then((response) => response.text())
      .then((response) => {
        const geoInfo = {
          remote_ip: getRowByPrefix(response, 'ip'),
          country_code: getRowByPrefix(response, 'loc'),
        };

        // Add breadcrumb for successful geo detection
        sentryService.addBreadcrumb('hardware', 'Geo info fetched', {
          countryCode: geoInfo.country_code,
          hasIp: !!geoInfo.remote_ip,
        });

        return geoInfo;
      })
      .catch((error) => {
        // Capture geo fetch failure as warning (non-critical)
        // Inner try-catch to prevent Sentry errors from propagating
        track('[KioskHardware] Sentry error reporting failed:', () => {
          sentryService.captureException(error, {
            tags: { component: 'KioskHardware', action: 'fetchGeoInfo' },
            extra: { cloudFlareApiUrl },
            level: 'warning',
          });
          sentryService.addBreadcrumb(
            'hardware',
            'Geo info fetch failed',
            {
              error: error.message,
            },
            'warning'
          );
        });
        return { remote_ip: null, country_code: null };
      });
  }

  getViewportInfo() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const isPortrait = width < height;
    const aspectRatio = height / width;

    // Vertical kiosk detection (fully dynamic):
    // 1. Portrait orientation (height > width)
    // 2. NOT a real mobile device (uses combined UA + screen size check)
    // 3. Large viewport height (≥1200px)
    // 4. Tall aspect ratio (>1.4)
    const isVerticalKiosk =
      isPortrait &&
      !this.isRealMobileDevice() && // Use robust check instead of just UA
      height >= 1200 &&
      aspectRatio > 1.4;

    return {
      width,
      height,
      isPortrait,
      isVerticalKiosk,
    };
  }

  updateGlobalCssClasses() {
    const root = document.querySelector(':root');
    const { isPortrait, isVerticalKiosk } = this.getViewportInfo();
    const displayProfile = isVerticalKiosk
      ? 'vertical'
      : isPortrait
        ? 'portrait'
        : 'landscape';
    const isMiniPc = this.getKioskMode() === this.MODES.MINI_PC_KIOSK;

    root.setAttribute(
      this.GLOBAL_DATA_ATTRIBUTES.DISPLAY_PROFILE,
      displayProfile
    );
    root.setAttribute(
      this.GLOBAL_DATA_ATTRIBUTES.RUNTIME,
      isMiniPc ? 'mini-pc' : 'virtual'
    );

    // Vertical kiosk is a DISTINCT mode - not combined with portrait
    // (portrait class is for mobile phones, vertical kiosk is for large displays)
    root.classList.toggle(
      this.GLOBAL_CLASSNAMES.VERTICAL_KIOSK,
      displayProfile === 'vertical'
    );
    root.classList.toggle(
      this.GLOBAL_CLASSNAMES.PORTRAIT,
      displayProfile === 'portrait'
    );
    root.classList.toggle(
      this.GLOBAL_CLASSNAMES.LANDSCAPE,
      displayProfile === 'landscape'
    );
    root.classList.toggle(this.GLOBAL_CLASSNAMES.MINI_PC, isMiniPc);
  }

  watchViewportResize() {
    // Track previous orientation for change detection
    let previousIsPortrait = this.viewportIsPortrait;
    let previousIsVerticalKiosk = this.isVerticalKiosk;

    const onWindowResize = () => {
      const { width, height, isPortrait, isVerticalKiosk } =
        this.getViewportInfo();

      // Detect orientation change
      const orientationChanged = previousIsPortrait !== isPortrait;
      const verticalKioskModeChanged =
        previousIsVerticalKiosk !== isVerticalKiosk;

      this.viewportWidth = width;
      this.viewportHeight = height;
      this.viewportIsPortrait = isPortrait;
      this.isVerticalKiosk = isVerticalKiosk;
      this.isMobileDevice = this.detectMobileUserAgent();
      this.updateGlobalCssClasses();

      // Only log Sentry breadcrumbs for SIGNIFICANT changes (orientation or mode)
      // to prevent buffer overflow during rapid resize events (window dragging)
      if (orientationChanged || verticalKioskModeChanged) {
        track('[KioskHardware] Sentry call failed:', () => {
          sentryService.addBreadcrumb(
            'hardware',
            'Viewport orientation/mode changed',
            {
              width,
              height,
              isPortrait,
              isVerticalKiosk,
              orientationChanged,
              verticalKioskModeChanged,
            }
          );

          if (orientationChanged) {
            sentryService.incrementCounter('hardware.orientation_change', 1, {
              new_orientation: isPortrait ? 'portrait' : 'landscape',
              kiosk_mode: this.kioskMode,
            });
          }
        });
      }

      // Update previous values for next comparison
      previousIsPortrait = isPortrait;
      previousIsVerticalKiosk = isVerticalKiosk;
    };
    // Store throttled listener reference for cleanup on unmount
    this.onWindowResizeThrottled = throttle(onWindowResize, 300);
    window.addEventListener('resize', this.onWindowResizeThrottled);
  }

  // Cleanup method for destroying this service
  destroy() {
    // Remove resize listener to prevent memory leaks and zombie event handlers
    if (this.onWindowResizeThrottled) {
      window.removeEventListener('resize', this.onWindowResizeThrottled);
      this.onWindowResizeThrottled = null;
    }

    // Cleanup Vue watch to prevent memory leaks
    if (this._unwatchDeveloperSettings) {
      this._unwatchDeveloperSettings();
      this._unwatchDeveloperSettings = null;
    }
  }

  watchDeveloperSettings() {
    const developer = useDeveloperStore();
    // Store the unwatch function for cleanup
    this._unwatchDeveloperSettings = watch(
      () => developer.forceKioskMode,
      (newMode, oldMode) => {
        this._applyKioskMode();
        this.updateGlobalCssClasses();

        // Add breadcrumb for developer mode override
        sentryService.addBreadcrumb(
          'hardware',
          'Developer forced kiosk mode change',
          {
            previousMode: oldMode,
            newMode,
            resolvedMode: this.kioskMode,
            isMiniPCKiosk: this.isMiniPCKiosk,
            isVirtualKiosk: this.isVirtualKiosk,
          },
          'info'
        );

        // Update Sentry tag for kiosk mode
        sentryService.setTag('kiosk_mode', this.kioskMode);
      }
    );
  }
}

export const kioskHardware = new KioskHardware();
